import moment from 'moment';
import { customEvents } from '../../../common/constants';
import { config } from '../config';
import { CartItem } from '../models/Cart';
import { ShowCartDropdownEventData } from '../models/CustomEvents';
import {
  PriceModel,
  ProductCardModel,
  ProductListModel,
} from '../models/Product';

export const rewriteImageUrl = (
  url?: string,
  width?: number,
  height?: number,
) => {
  if (!url?.length) {
    return '';
  }

  return !!width && !!height
    ? `${config.imagesUrl}/${url}?width=${width}&height=${height}`
    : `${config.imagesUrl}/${url}`;
};

export const parseProducts = (
  products?: ProductListModel[],
  isBrandType?: boolean,
): ProductCardModel[] | undefined => {
  if (!products) return undefined;

  if (!isBrandType) {
    return products.map(
      p =>
        ({
          name: p.name,
          productId: p.productId,
          imagePath: p.imagePath,
          secretImagePath: p.secretImagePath,
          isFavorite: p.isFavorite,
          path: p.path,
          isSecret: p.isSecret,
          adventDate: p.adventDate,
          price: p.price,
          cashBackValue: p.cashBackValue,
          buyerType: p.buyerType,
          brandSortOrder: p.brandSortOrder,
          manufacturerId: p.manufacturerId,
          views: p.views,
          dateAdded: p.dateAdded,
          isGroupedByBrand: false,
          hidePriceAndTaxes: p.hidePriceAndTaxes,
          dyDecisionId: p.dyDecisionId,
          dySlotId: p.dySlotId,
          searchScore: p.searchScore,
        }) as ProductCardModel,
    );
  }

  const grouped = groupProductsByBrand(products);
  if (!grouped) return undefined;

  return Object.entries(grouped)
    .map(([brandId, group]) => {
      const totalViews = group.reduce((sum, p) => sum + p.views, 0);
      const productCount = group.length;
      const avgViews = totalViews / productCount;

      const commonPrice = getCommonPrice(group);
      const priceRanges = group.flatMap(p => {
        const price = p.price.discountedPrice ?? p.price.defaultPrice;

        if (price > 0) {
          return [price];
        }

        return p.price.priceRanges ?? [0, 0];
      });

      return {
        name: group[0].brandName,
        productId: group.length === 1 ? group[0].productId : undefined,
        brandId: Number(brandId),
        imagePath: group[0].brandImagePath,
        manufacturerId: group[0].manufacturerId,
        brandSortOrder: group[0].brandSortOrder,
        path: group[0].path,
        isFavorite: false,
        isSecret: false,
        views: avgViews,
        dateAdded: getEarliestDate(group),
        price: {
          defaultPrice: commonPrice.defaultPrice,
          discountedPrice: commonPrice.discountedPrice,
          priceRanges: getEdgePrices(priceRanges),
        },
        buyerType: null,
        isGroupedByBrand: true,
        hidePriceAndTaxes: group.every(p => p.hidePriceAndTaxes),
      };
    })
    .sort((a, b) => {
      // 1. Products with manufacturerSortOrder = 0 are pushed to the end of the list
      if (a.brandSortOrder === 0 && b.brandSortOrder !== 0) return 1;
      if (b.brandSortOrder === 0 && a.brandSortOrder !== 0) return -1;

      // 2. Primary sorting: by manufacturerSortOrder in ascending order
      if (a.brandSortOrder !== b.brandSortOrder) {
        return a.brandSortOrder - b.brandSortOrder;
      }

      // 3. Tie-breaker: sort by average views in descending order
      return b.views - a.views;
    });
};

const groupProductsByBrand = (products: ProductListModel[]) => {
  if (products && products.length > 0)
    return products.reduce(
      (groupedProducts, product) => {
        const { brandId } = product;

        if (!groupedProducts[brandId]) {
          groupedProducts[brandId] = [];
        }

        groupedProducts[brandId].push(product);

        return groupedProducts;
      },
      {} as Record<number, ProductListModel[]>,
    );
};

const getEarliestDate = (products: ProductListModel[]): Date => {
  return products.reduce(
    (earliest, product) => {
      return product.dateAdded < earliest ? product.dateAdded : earliest;
    },
    products[0]?.dateAdded,
  );
};

const getCommonPrice = (products: ProductListModel[]): PriceModel => {
  let bestDiscountedPrice = { ...products[0].price };
  let maxDiscount = 0;

  products.forEach(product => {
    const { defaultPrice, discountedPrice } = product.price;

    if (discountedPrice) {
      const discount = (100 * (defaultPrice - discountedPrice)) / defaultPrice;

      if (discount > maxDiscount) {
        maxDiscount = discount;
        bestDiscountedPrice = product.price;
      }
    }
  });

  return bestDiscountedPrice;
};

const getEdgePrices = (priceRange: number[]): number[] => {
  const minPrice = Math.min(...priceRange);
  const maxPrice = Math.max(...priceRange);

  return [minPrice, maxPrice];
};

export const dispatchShowCartDropdownEvent = (cartItems: CartItem[]) => {
  const event = new CustomEvent<ShowCartDropdownEventData>(
    customEvents.showCartDropdown,
    {
      detail: { cartItems },
    },
  );
  document.dispatchEvent(event);
};

export const wait = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

export const toUtcStartOfDay = (value: Date | string): Date => {
  const calendarDate = moment(value).format('YYYY-MM-DD');
  return moment.utc(calendarDate, 'YYYY-MM-DD').startOf('day').toDate();
};
